// client paginations
export const DEFAULT_PAGINATION_LIMIT = 25;

// redis pubsub notification topic
export const NOTIFICATIONS_UPDATED_TOPIC = 'notifications_updated_topic';
export const RECORDING_EVENT_TOPIC = 'recording_event_topic';
export const RECORDING_COMPLETED_TOPIC = 'recording_completed_topic';
export const GIF_GENERATED_TOPIC = 'gif_generated_topic';
export const WAVEFORM_GENERATION_TOPIC = 'waveform_generation_topic';
export const SESSION_REQUEST_TOKEN_GENERATED_TOPIC =
  'session_request_token_generated_topic';

export enum DeliveryMethods {
  Mail = 'mail',
  Mobile = 'mobile',
  Web = 'web',
  Slack = 'slack',
}

// TODO: Refactor so that we use DeliveryMethods enum
// instead of these exports
export const DELIVERY_MAIL = DeliveryMethods.Mail;
export const DELIVERY_MOBILE = DeliveryMethods.Mobile;
export const DELIVERY_WEB = DeliveryMethods.Web;
export const DELIVERY_SLACK = DeliveryMethods.Slack;

export const DELIVERY_METHODS_AVAILABLE = Object.values(DeliveryMethods);

// paginated GQL args
export const QUERY_SHARED = 'shared';
export const QUERY_COMMENT = 'comments';
export const QUERY_REACTIONS_AND_VIEWS = 'reactions_and_views';
export const QUERY_OTHER = 'other';
export const QUERY_ALL = 'all';

// Push service constants
export const PUSH_SERVER_CONTACT_EMAIL = 'mailto:team@loom.com';
export const INVALID_SUBSCRIPTION_ERROR = 'InvalidStateError';

// Client settings
export const PERMISSION_GRANTED = 'granted';
export const PERMISSION_DENIED = 'denied';
export const PERMISSION_DEFAULT = 'default'; // equivalent to denied wtf
export const PERMISSION_PROMPT = 'prompt';
export const DEVICE_UNSUPPORTED = 'device_unsupported';

// notification status
export const CREATED = 'created';
export const SENT = 'sent';
export const DELIVERED = 'delivered';
export const SEEN = 'seen';
export const READ = 'read';
export const NOTIF_STATUSES = [CREATED, SENT, DELIVERED, SEEN, READ];

// service worker actions
export const OPEN_URL = 'open_url';

export const NOTIFICATION_URL_PARAMS = {
  reactor: 'user_id_of_reactor',
  startAndPause: 'start_and_pause',
};

export enum NotificationType {
  VideoUsedAsWeaveClip = 'video_used_as_weave_clip',

  CommentReply = 'comment_reply_notification',
  FirstVideoView = 'first_video_view_notification',
  WeaveFirstVideoView = 'weave_video_first_view',
  AddWatchLater = 'added_to_watch_later_notification',
  VideoComment = 'video_comment_notification',
  WeeklyDigest = 'weekly_digest_notification',
  VideoReaction = 'video_reaction_notification',
  ContentLimitApproaching = 'content_limit_approaching_notification',
  ContentLimitReached = 'content_limit_reached_notification',
  CreatorLiteLimitApproaching = 'creator_lite_limit_approaching_notification',
  CreatorLiteLimitReached = 'creator_lite_limit_reached_notification',
  MembershipRoleChanged = 'membership_role_change_notification',
  WorkspaceInvitation = 'workspace_invitation_notification',
  ShareVideo = 'share_video_notification',
  ReshareVideo = 'reshare_video_notification',
  VideoPrivacyChange = 'video_privacy_change_notification',
  ExternalVideoIngestionProcessing = 'external_ingestion_processing_notification',
  ExternalVideoIngestionCompleted = 'external_ingestion_completed_notification',
  IngestionIntegrationEnabled = 'ingestion_integration_enabled_notification',

  CommentMention = 'comment_mention_notification',
  PostCommentMention = 'post_comment_mention_notification',
  ReplyCommentMention = 'reply_comment_mention_notification',
  VideoReactionBundle = 'videoReactionBundle',

  // TODO: Can be deprecated?
  CreatorLimitApproaching = 'creator_limit_approaching_notification',
  CreatorLimitReached = 'creator_limit_reached_notification',
}

// DEPRECATED -  Do not update. Use 'NotificationType' enum instead
// For older notifications only and only used on the frontend
// Usage of this enum will be removed in the future
export enum NotificationClientType {
  Reply = 'reply',
  Comment = 'comment',
  Reaction = 'reaction',
  FirstVideoView = 'vfv',
  WatchLater = 'watchList',
  ContentLimitApproaching = 'contentLimitApproaching',
  ContentLimitReached = 'contentLimitReached',
  CreatorLiteLimitApproaching = 'creatorLimitApproaching',
  CreatorLiteLimitReached = 'creatorLimitReached',
  MembershipRoleChange = 'membershipRoleChange',
  ShareVideo = 'shareVideo',
  ReshareVideo = 'reshareVideo',
  VideoPrivacyChange = 'videoPrivacyChange',
  PostCommentMention = 'postCommentMention',
  ReplyCommentMention = 'replyCommentMention',
  ExternalIngestionProcessing = 'externalIngestionProcessing',
  ExternalIngestionCompleted = 'externalIngestionCompleted',

  CommentMention = 'commentMention',
  WeeklyDigest = 'weeklyDigest',
  WorkspaceInvitation = 'workspaceInvitation',

  // TODO: Can be deprecated
  CreatorLimitApproaching = 'creatorLimitApproaching',
  CreatorLimitReached = 'creatorLimitReached',
}

const VIDEO_REACTION_EVENT = 'Video Reaction Push Notification Sent';

type NotificationConfigItem = {
  // This is the primary type name of the notification
  // This is used mainly on the backend to reference the
  // notification (and in a few parts on the frontend).
  // This type is what we persist on the DB to identify the notification
  type: NotificationType;

  // Used primarly on the frontend.
  // These can be different than 'type' for older notifications.
  // For new notifications use a `NotificationType` as we will
  // deprecate NotificationClientType in the future
  clientName: NotificationType | NotificationClientType;

  // The name of the analytics event triggered
  // when the notification is sent.
  sentEvent: string;

  // The name of the analytics event triggered
  // when the web push notification is clicked
  // (REQUIRED for 'web' delivery methods)
  webPushEvent?: string;

  // Overrides if this notification
  // can be toggled on/off from the Notification Settings UI
  // Default is `false`
  //
  // A migration is needed to add your NotificationType to
  // the notification_settings DB table before this can be enabled
  canToggle?: boolean;

  // Overrides which delivery methods are allowed.
  // If omitted the default enables all methods
  deliveryMethods?: DeliveryMethods[];

  // By default all of these are enabled
  // Add a key for any delivery method you
  // want to override to off/false
  deliveryMethodOverrides?: {
    [DELIVERY_WEB]?: boolean;
    [DELIVERY_MOBILE]?: boolean;
    [DELIVERY_MAIL]?: boolean;
  };
};

const VIDEO_REACTION_PUSH_NOTIF_CLICK = 'Video Reaction Push Notif Click';

// TODO: Can we or should we move all this into a /config.js file instead to keep this file even shorter?
// Or move the other stuff if that's easier

// TODO: At some point we should get rid of 'clientName' and just use 'type' everywhere
// The clients need to be updated first before we can remove it
export const NOTIFICATIONS_CONFIGURATION: {
  [key in NotificationType]: NotificationConfigItem;
} = {
  [NotificationType.VideoUsedAsWeaveClip]: {
    type: NotificationType.VideoUsedAsWeaveClip,
    clientName: NotificationType.VideoUsedAsWeaveClip,
    sentEvent: 'Video Used as Weave Clip Notification',
    webPushEvent: 'Video Used As Weave Clip Click',
    canToggle: true,
  },

  [NotificationType.CommentReply]: {
    type: NotificationType.CommentReply,
    clientName: NotificationClientType.Reply,
    sentEvent: 'Comment Reply Push Notification Sent',
    webPushEvent: 'Comment Reply Push Notif Click',
    canToggle: true,
  },

  [NotificationType.ContentLimitReached]: {
    type: NotificationType.ContentLimitReached,
    clientName: NotificationClientType.ContentLimitReached,
    sentEvent: 'Content Limit Reached Notification Sent',
    webPushEvent: 'Reached Content Limit Notif Click',
  },

  [NotificationType.CreatorLimitApproaching]: {
    type: NotificationType.CreatorLimitApproaching,
    clientName: NotificationClientType.CreatorLimitApproaching,
    sentEvent: 'Creator Limit Approaching Notification Sent', // remove if can be deprecated
    webPushEvent: 'Approaching Creator Limit Notif Click',
  },

  [NotificationType.CreatorLiteLimitApproaching]: {
    type: NotificationType.CreatorLiteLimitApproaching,
    clientName: NotificationClientType.CreatorLiteLimitApproaching,
    sentEvent: 'Creator Lite Limit Approaching Notification Sent',
    webPushEvent: 'Approaching Creator Lite Limit Notif Click',
  },

  [NotificationType.CreatorLiteLimitReached]: {
    type: NotificationType.CreatorLiteLimitReached,
    clientName: NotificationClientType.CreatorLiteLimitReached,
    sentEvent: 'Creator Lite Limit Reached Notification Sent',
    webPushEvent: 'Reached Creator Lite Limit Notif Click',
  },

  [NotificationType.FirstVideoView]: {
    type: NotificationType.FirstVideoView,
    clientName: NotificationClientType.FirstVideoView,
    sentEvent: 'First Video View Push Notificaiton Sent',
    webPushEvent: 'First Video View Push Notif Click',
    canToggle: true,
  },

  [NotificationType.WeaveFirstVideoView]: {
    type: NotificationType.WeaveFirstVideoView,
    clientName: NotificationType.WeaveFirstVideoView,
    sentEvent: 'Weave First Video View Push Notificaiton Sent',
    webPushEvent: 'Weave First Video View Push Notif Click',
    canToggle: true,
  },

  [NotificationType.VideoComment]: {
    type: NotificationType.VideoComment,
    clientName: NotificationClientType.Comment,
    sentEvent: 'Video Comment Push Notification Sent',
    webPushEvent: 'Video Comment Push Notif Click',
    canToggle: true,
  },

  // TODO: Can we delete this? I don't see
  // a record of this being used anymore
  [NotificationType.WeeklyDigest]: {
    type: NotificationType.WeeklyDigest,
    clientName: NotificationClientType.WeeklyDigest,
    sentEvent: 'Weekly Digest Notification Sent',
  },

  [NotificationType.WorkspaceInvitation]: {
    type: NotificationType.WorkspaceInvitation,
    clientName: NotificationClientType.WorkspaceInvitation,
    sentEvent: 'Workspace Inivitation Notification Sent',
    deliveryMethods: [DeliveryMethods.Mail],
  },

  [NotificationType.VideoReaction]: {
    type: NotificationType.VideoReaction,
    clientName: NotificationClientType.Reaction,
    sentEvent: VIDEO_REACTION_EVENT,
    webPushEvent: VIDEO_REACTION_PUSH_NOTIF_CLICK,
    canToggle: true,
    deliveryMethodOverrides: {
      [DELIVERY_MAIL]: false,
    },
  },

  [NotificationType.MembershipRoleChanged]: {
    type: NotificationType.MembershipRoleChanged,
    clientName: NotificationClientType.MembershipRoleChange,
    sentEvent: 'Membership Role Change Notification Sent',
  },

  [NotificationType.AddWatchLater]: {
    type: NotificationType.AddWatchLater,
    clientName: NotificationClientType.WatchLater,
    sentEvent: 'Added to Watch Later Push Notification Sent',
    webPushEvent: 'Watch Later Push Notification Click',
    canToggle: true,
  },

  [NotificationType.ShareVideo]: {
    type: NotificationType.ShareVideo,
    clientName: NotificationClientType.ShareVideo,
    sentEvent: 'Video Share Push Notification Sent',
    webPushEvent: 'Video Share Push Notif Click',
    canToggle: true,
  },

  [NotificationType.ReshareVideo]: {
    type: NotificationType.ReshareVideo,
    clientName: NotificationClientType.ReshareVideo,
    sentEvent: 'Video Re-share Push Notification Sent',
    webPushEvent: 'Video Re-share Push Notif Click',
    canToggle: true,
    deliveryMethodOverrides: {
      [DELIVERY_MAIL]: false,
    },
  },

  [NotificationType.VideoPrivacyChange]: {
    type: NotificationType.VideoPrivacyChange,
    clientName: NotificationClientType.VideoPrivacyChange,
    sentEvent: 'Video Privacy Change Push Notification Sent',
    webPushEvent: 'Video Privacy Change Push Notif Click',
    canToggle: true,
    deliveryMethodOverrides: {
      [DELIVERY_MAIL]: false,
    },
  },

  [NotificationType.PostCommentMention]: {
    type: NotificationType.PostCommentMention,
    clientName: NotificationClientType.PostCommentMention,
    sentEvent: 'Post Comment Mention Notification Sent',
    webPushEvent: 'Comment Mention Push Notif Click',
  },

  [NotificationType.ReplyCommentMention]: {
    type: NotificationType.ReplyCommentMention,
    clientName: NotificationClientType.ReplyCommentMention,
    sentEvent: 'Reply Comment Mention Notification Sent',
    webPushEvent: 'Reply Comment Mention Push Notif Click',
  },

  [NotificationType.ExternalVideoIngestionProcessing]: {
    type: NotificationType.ExternalVideoIngestionProcessing,
    clientName: NotificationClientType.ExternalIngestionProcessing,
    sentEvent: 'Extenal Video Ingestion Processing Notification Sent',
    webPushEvent: 'External Ingestion Processing Notif Click',
    canToggle: true,
  },

  [NotificationType.ExternalVideoIngestionCompleted]: {
    type: NotificationType.ExternalVideoIngestionCompleted,
    clientName: NotificationClientType.ExternalIngestionCompleted,
    sentEvent: 'Extenal Video Ingestion Completed Notification Sent',
    webPushEvent: 'External Ingestion Completed Notif Click',
    canToggle: true,
  },

  [NotificationType.IngestionIntegrationEnabled]: {
    type: NotificationType.IngestionIntegrationEnabled,
    clientName: NotificationType.IngestionIntegrationEnabled,
    sentEvent: 'Ingestion Integration Enabled Notification Sent',
    webPushEvent: 'Ingestion Integration Enabled Notif Click',
  },

  [NotificationType.ContentLimitApproaching]: {
    type: NotificationType.ContentLimitApproaching,
    clientName: NotificationClientType.ContentLimitApproaching,
    sentEvent: 'Content Limit Approaching Notification Sent',
    webPushEvent: 'Approaching Content Limit Notif Click',
  },

  [NotificationType.CreatorLimitReached]: {
    type: NotificationType.CreatorLimitReached,
    clientName: NotificationClientType.CreatorLimitReached,
    sentEvent: 'Creator Limit Reached Notification Sent', // remove if can be deprecated
    webPushEvent: 'Reached Creator Limit Notif Click', // remove if can be deprecated,
  },

  // TODO: Is this still used? May need to keep for legacy reasons
  [NotificationType.CommentMention]: {
    type: NotificationType.CommentMention,
    clientName: NotificationClientType.CommentMention,
    sentEvent: 'Comment Mention Notification Sent',
    canToggle: true,
  },

  // This notification is a special notification
  // that mirrors the regular reaction notificaiton
  [NotificationType.VideoReactionBundle]: {
    type: NotificationType.VideoReactionBundle,
    clientName: NotificationClientType.Reaction,
    sentEvent: VIDEO_REACTION_EVENT,
    webPushEvent: VIDEO_REACTION_PUSH_NOTIF_CLICK,
  },
};

export const NOTIFICATION_TYPES = Object.values(NotificationType).filter(
  notifType => {
    return ![
      // Filter out this notification as it is a special type
      // of 'reaction' notification
      NotificationType.VideoReactionBundle,
      // These notifications are not saved in the DB
      NotificationType.WorkspaceInvitation,
      NotificationType.CommentMention,
    ].includes(notifType);
  }
);

// These are notifications that users
// are able to toggle from their account settings
export const TOGGLE_NOTIFICATION_TYPES = Object.values(
  NOTIFICATIONS_CONFIGURATION
)
  .filter(({ canToggle }) => canToggle)
  .map(({ type }) => type);

export const NOTIFICATION_CLIENT_NAMES = Object.values(
  NOTIFICATIONS_CONFIGURATION
).map(({ clientName }) => clientName);

// DEPRECATED
// OLDER NOTIFICATION CONSTANTS. Use `NotificationType` enum going forward
export const COMMENT_REPLY_NOTIFICATION = NotificationType.CommentReply;
export const FIRST_VIDEO_VIEW_NOTIFICATION = NotificationType.FirstVideoView;
export const ADDED_TO_WATCH_LATER_NOTIFICATION = NotificationType.AddWatchLater;
export const VIDEO_COMMENT_NOTIFICATION = NotificationType.VideoComment;
export const WEEKLY_DIGEST_NOTIFICATION = NotificationType.WeeklyDigest;
export const VIDEO_REACTION_NOTIFICATION = NotificationType.VideoReaction;
export const CONTENT_LIMIT_APPROACHING_NOTIFICATION =
  NotificationType.ContentLimitApproaching;
export const CONTENT_LIMIT_REACHED_NOTIFICATION =
  NotificationType.ContentLimitReached;
export const CREATOR_LITE_LIMIT_APPROACHING_NOTIFICATION =
  NotificationType.CreatorLiteLimitApproaching;
export const CREATOR_LITE_LIMIT_REACHED_NOTIFICATION =
  NotificationType.CreatorLiteLimitReached;
export const MEMBERSHIP_ROLE_CHANGE_NOTIFICATION =
  NotificationType.MembershipRoleChanged;
export const WORKSPACE_INVITATION_NOTIFICATION =
  NotificationType.WorkspaceInvitation;
export const SHARE_VIDEO_NOTIFICATION = NotificationType.ShareVideo;
export const RESHARE_VIDEO_NOTIFICATION = NotificationType.ReshareVideo;
export const VIDEO_PRIVACY_CHANGE_NOTIFICATION =
  NotificationType.VideoPrivacyChange;
export const EXTERNAL_VIDEO_INGESTION_PROCESSING_NOTIFICATION =
  NotificationType.ExternalVideoIngestionProcessing;
export const EXTERNAL_VIDEO_INGESTION_COMPLETED_NOTIFICATION =
  NotificationType.ExternalVideoIngestionCompleted;

// TODO: Can be deprecated?
export const CREATOR_LIMIT_APPROACHING_NOTIFICATION =
  NotificationType.CreatorLimitApproaching;
export const CREATOR_LIMIT_REACHED_NOTIFICATION =
  NotificationType.CreatorLimitReached;

// Consolidation of all mention notification under one setting
export const COMMENT_MENTION_NOTIFICATION = NotificationType.CommentMention;
export const POST_COMMENT_MENTION_NOTIFICATION =
  NotificationType.PostCommentMention;
export const REPLY_COMMENT_MENTION_NOTIFICATION =
  NotificationType.ReplyCommentMention;

// This is only used internally in this module
export const VIDEO_REACTION_BUNDLE_NOTIFICATION =
  NotificationType.VideoReactionBundle;
